import {createStore} from 'vuex'

/**
 * Строит функцию конвертации индексов из Unicode code-points
 * (как считает PHP mb_strlen) в UTF-16 code units (как считает JS string.length / Quill).
 *
 * Символы за пределами BMP (эмодзи и др.) занимают 1 code point, но 2 UTF-16 code units.
 * Если в тексте нет таких символов, возвращает identity-функцию (O(1)).
 * Иначе — предвычисляет таблицу за O(n), потом каждый lookup за O(1).
 */
function buildCpToUtf16Map(text) {
    const codePoints = [...text]; // Array.from — итерация по code points
    if (codePoints.length === text.length) {
        // Нет символов за пределами BMP — позиции совпадают
        return (cpIdx) => cpIdx;
    }

    // map[i] = UTF-16 позиция, соответствующая code-point позиции i
    const map = new Array(codePoints.length + 1);
    map[0] = 0;
    for (let i = 0; i < codePoints.length; i++) {
        map[i + 1] = map[i] + codePoints[i].length; // .length = 1 для BMP, 2 для surrogate pair
    }

    return (cpIdx) => {
        if (cpIdx <= 0) return 0;
        if (cpIdx >= map.length) return text.length;
        return map[cpIdx];
    };
}

const store = createStore({
    state: {
        tips: [],
        activeTip: Object,
        activeTipFocused: false,
        bounds: [],
        isTextInserted: false,
    },
    mutations: {
        clearTips(state) {
            state.tips = []
        },
        addTip(state, tip) {
            state.tips.push(tip)
        },
        dropTip(state, tip) {
            let idx = state.tips.find((el) => {return el.id === tip.id});
            state.tips.splice(idx, 1)
        },
        setActiveTip(state, tip) {
            state.tips.forEach((el, i) => {
                state.tips[i].active = el.id === tip.id
            })
            state.activeTip = tip
        },
        dropActiveTip(state) {
            state.tips.forEach((el, i) => {
                state.tips[i].active = false
            })
            state.activeTip = null
        },
        clearBounds(state) {
            state.bounds = []
        },
        addBounds(state, bounds) {
            state.bounds.push(bounds)
        },
        setIsTextInserted(state, isTextInserted) {
            state.isTextInserted = isTextInserted
        },
        setActiveTipFocused(state, isFocused) {
            state.activeTipFocused = isFocused
        },
        setTips(state, tips) {
            state.tips = tips
        }
    },
    actions: {
        clearTips(context) {
            context.commit('clearTips')
        },
        async fetchTips(context, { htmlText, plainText }) {
            if (plainText.length <= 1) {
                context.commit('clearTips')
                return
            }

            const cpToUtf16 = buildCpToUtf16Map(plainText);

            await fetch('/neatify/backend/api/v3/proofread/', {
                method: 'POST',
                body: JSON.stringify({text: htmlText}),
                headers: {
                    "Content-Type": "application/json"
                }
           })
                .then(response => response.json())
                .then(data => {
                    context.commit('clearTips')
                    if (!Array.isArray(data)) return // debug mode fallback
                    for (let i = 0; i < data.length; i++) {
                        let currTip = []
                        currTip.id = data[i].id
                        currTip.mark = data[i].mark
                        currTip.title = data[i].title
                        currTip.description = data[i].description
                        // Сохраняем ОБОИ системы координат:
                        // cp* — code points (то, что отдаёт PHP), для /fix endpoint
                        // index/size — UTF-16 (для Quill retain/delete/getBounds)
                        currTip.cpIndex = data[i].index
                        currTip.cpSize = data[i].size
                        const utf16Start = cpToUtf16(data[i].index);
                        const utf16End = cpToUtf16(data[i].index + data[i].size);
                        currTip.index = utf16Start
                        currTip.size = utf16End - utf16Start
                        currTip.correct = data[i].correct
                        currTip.top = 0
                        currTip.position = 0
                        if (store.getters.activeTip !== null) {
                            currTip.active = data[i].id === store.getters.activeTip.id
                        } else {
                            currTip.active = false
                        }
                        context.commit('addTip', currTip)
                    }
                })
        },
        /**
         * Применить исправление на бэкенде.
         * Бэкенд: заменяет [cp_index, cp_index+cp_size) на replacement,
         *         пересчитывает все issues на новом тексте.
         * Возвращает {text, issues}.
         */
        async fixTip(context, {text, tip}) {
            const replacement = Array.isArray(tip.correct) ? tip.correct.join('') : '';
            const resp = await fetch('/neatify/backend/api/v3/proofread/', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'fix',
                    text: text,
                    cp_index: tip.cpIndex,
                    cp_size: tip.cpSize,
                    replacement: replacement,
                }),
                headers: { "Content-Type": "application/json" }
            });
            
            if (!resp.ok) {
                const error = await resp.json().catch(() => ({error: 'Ошибка сервера'}));
                throw new Error(error.error || `HTTP ${resp.status}`);
            }
            
            return await resp.json();
        },
        /**
         * Применить все исправления с тем же mark на бэкенде.
         *
         * Передаём массив fixes (cp_index, cp_size, replacement) по всем советам с данным mark,
         * т.к. при повторном прогоне правил на бэкенде mark меняется (checkTime) и поиск по mark не находит совпадений.
         */
        async fixAllTips(context, {text, fixes}) {
            const resp = await fetch('/neatify/backend/api/v3/proofread/', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'fix_all',
                    text: text,
                    fixes: fixes,
                }),
                headers: { "Content-Type": "application/json" }
            });
            
            if (!resp.ok) {
                const error = await resp.json().catch(() => ({error: 'Ошибка сервера'}));
                throw new Error(error.error || `HTTP ${resp.status}`);
            }
            
            return await resp.json();
        },
        dropTip(context, payload) {
            context.commit('dropTip', payload)
        },
        setActiveTip(context, payload) {
            context.commit('setActiveTip', payload)
        },
        dropActiveTip(context) {
            context.commit('dropActiveTip')
        },
        clearBounds(context) {
            context.commit('clearBounds')
        },
        addBounds(context, payload) {
            context.commit('addBounds', payload)
        },
        setIsTextInserted(context, payload) {
            context.commit('setIsTextInserted', payload)
        },
        setActiveTipFocused(context, payload) {
            context.commit('setActiveTipFocused', payload)
        },
        setLocalTips(context, payload) {
            context.commit('setTips', payload)
        }
    },
    getters: {
        tips(state) {
            return state.tips
        },
        activeTip(state) {
            return state.activeTip
        },
        bounds(state) {
            return state.bounds
        },
        isTextInserted(state) {
            return state.isTextInserted
        },
        activeTipFocused(state) {
            return state.activeTipFocused
        }
    }
})

export { buildCpToUtf16Map }
export default store
